import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Mail, MapPin, ArrowRight } from "lucide-react";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", { kind: "contact", ...form });
      toast.success("Message sent! We'll get back to you soon.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <section className="max-w-7xl mx-auto px-5 md:px-10 pt-16 md:pt-24 pb-8">
        <span className="text-xs uppercase tracking-wider font-semibold text-[#FF9F1C]">Contact</span>
        <h1 className="mt-3 font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none">Say hi. We love new cubers.</h1>
        <p className="mt-6 text-lg text-[#7A7D8C] max-w-2xl">Questions about competitions, camp, or getting started? Send a note and one of our organizers will reply within a day or two.</p>
      </section>

      <section className="max-w-7xl mx-auto px-5 md:px-10 pb-24 grid lg:grid-cols-5 gap-8">
        <div className="lg:col-span-2 space-y-5">
          <InfoTile icon={<Mail className="w-5 h-5" />} title="Email" value="naptowncubers@gmail.com" />
          <InfoTile icon={<MapPin className="w-5 h-5" />} title="Where we play" value="Annapolis, MD — event venues confirmed with each registration." />
          <div className="rounded-3xl bg-[#FDE74C]/30 p-6 border border-[#FDE74C]/40">
            <h4 className="font-display font-bold">Come out to an event</h4>
            <p className="text-sm text-[#1E1E24]/90 mt-2 leading-relaxed">Not sure yet? Come watch a competition first. Bring a cube, cheer for the kids, catch the vibe.</p>
          </div>
        </div>

        <form onSubmit={submit} className="lg:col-span-3 bg-white rounded-[32px] border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-8 md:p-10 space-y-5" data-testid="contact-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Your name" value={form.name} required onChange={(v) => setForm({ ...form, name: v })} tid="contact-name" />
            <Field label="Email" type="email" value={form.email} required onChange={(v) => setForm({ ...form, email: v })} tid="contact-email" />
          </div>
          <Field label="Phone (optional)" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} tid="contact-phone" />
          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-[#7A7D8C]">Message</label>
            <textarea data-testid="contact-message" required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1 w-full rounded-xl border border-slate-200 bg-[#FDFFFC] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF9F1C] focus:border-[#FF9F1C]" />
          </div>
          <button type="submit" disabled={submitting} data-testid="contact-submit" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#FF9F1C] text-white font-bold shadow-[0_12px_30px_rgb(255,159,28,0.35)] hover:-translate-y-0.5 transition-transform disabled:opacity-60">
            {submitting ? "Sending…" : "Send message"} <ArrowRight className="w-4 h-4" />
          </button>
        </form>
      </section>
    </div>
  );
}

function InfoTile({ icon, title, value }) {
  return (
    <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
      <div className="w-11 h-11 rounded-2xl bg-[#FF9F1C] text-white grid place-items-center">{icon}</div>
      <div className="mt-4 text-xs uppercase tracking-wider text-[#7A7D8C] font-semibold">{title}</div>
      <div className="mt-1 text-[#1E1E24] font-semibold">{value}</div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, tid }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wider font-semibold text-[#7A7D8C]">{label}</label>
      <input data-testid={tid} type={type} value={value} required={required} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full rounded-xl border border-slate-200 bg-[#FDFFFC] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF9F1C] focus:border-[#FF9F1C]" />
    </div>
  );
}
